import React, { useState, useEffect } from 'react';
import FormService from '../services/FormService';
import AnalyticsService from '../services/AnalyticsService';
import './ProjectStartForm.css';

const ProjectStartForm = ({ sectionId, address, startOpen = false, origin = 'Urban Renewal Area', pageMode = false }) => {
  const [showForm, setShowForm] = useState(startOpen || pageMode);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    origin: origin, // Add origin to form data
    address: address || '',
    floors: '',
    apartments: '',
    tenantRepresentation: '',
    previousAttempt: '',
    appointedRoles: [],
    developerInvolved: '',
    developerDetails: '',
    fullName: '',
    email: '',
    phone: '',
    termsAccepted: false,
    updatesAccepted: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleRoleToggle = (role) => {
    setFormData(prev => ({
      ...prev,
      appointedRoles: prev.appointedRoles.includes(role)
        ? prev.appointedRoles.filter(r => r !== role)
        : [...prev.appointedRoles, role]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.floors || parseInt(formData.floors) < 1) {
      newErrors.floors = 'נא למלא מספר קומות תקין';
    }
    if (!formData.apartments || parseInt(formData.apartments) < 1) {
      newErrors.apartments = 'נא למלא מספר דירות תקין';
    }
    if (!formData.tenantRepresentation) {
      newErrors.tenantRepresentation = 'שדה חובה';
    }
    if (!formData.previousAttempt) {
      newErrors.previousAttempt = 'שדה חובה';
    }
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'שדה חובה';
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'כתובת אימייל לא תקינה';
    }
    if (!formData.phone.trim() || !/^[0-9]{9,15}$/.test(formData.phone.replace(/[-\s]/g, ''))) {
      newErrors.phone = 'מספר טלפון לא תקין';
    }
    if (!formData.termsAccepted) {
      newErrors.termsAccepted = 'יש לאשר את תנאי השימוש';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Send data to backend (Fire and forget)
    console.log('📋 Submitting project start form:', formData);
    FormService.submitForm(`Project Start Form - ${origin}`, formData)
      .catch(err => console.error('Failed to submit form (background):', err));
    AnalyticsService.trackProjectStartSubmitted({
      sourceSurface: 'project_start_form',
      address: formData.address,
      municipality: null,
    });

    setIsSubmitting(false);
    setShowSuccess(true);
  };

  useEffect(() => {
    if (showSuccess) {
      // Scroll to top to prevent header glitch and show message
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Small timeout to ensure DOM is ready
      const timer = setTimeout(() => {
        createConfetti();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  const createConfetti = () => {
    const colors = ['#26ccff', '#a25afd', '#ff5e7e', '#88ff5a', '#fcff42', '#ffa62d', '#ff36ff'];
    const confettiCount = 50;
    const container = document.querySelector('.success-screen');
    
    if (!container) return;

    for (let i = 0; i < confettiCount; i++) {
      const confetti = document.createElement('div');
      confetti.className = 'confetti';
      confetti.style.background = colors[Math.floor(Math.random() * colors.length)];
      confetti.style.left = Math.random() * 100 + '%';
      confetti.style.animationDuration = (Math.random() * 3 + 2) + 's';
      confetti.style.animationDelay = (Math.random() * 2) + 's';
      container.appendChild(confetti);
    }
  };

  if (showSuccess) {
    return (
      <div id={sectionId} className="project-start-section success-view">
        <div className="success-screen">
          <div className="success-icon-wrapper">
            <div className="success-circle"></div>
            <i className="fi fi-rr-check success-check"></i>
          </div>
          <h2 className="success-title">תודה! הפרטים התקבלו בהצלחה</h2>
          <p className="success-message">נעדכן אתכם ברגע שנהיה מוכנים עם כלים חדשים לקידום תהליך ההתחדשות.</p>
          
          <div className="next-steps-timeline">
            <div className="timeline-step completed">
              <div className="step-icon"><i className="fi fi-rr-check"></i></div>
              <div className="step-content">
                <span className="step-title">הפרטים נשמרו</span>
                <span className="step-desc">נרשמת לעדכונים</span>
              </div>
            </div>
            <div className="timeline-line active"></div>
            <div className="timeline-step active">
              <div className="step-icon"><i className="fi fi-rr-hammer"></i></div>
              <div className="step-content">
                <span className="step-title">אנחנו בונים</span>
                <span className="step-desc">כלים חדשים בפיתוח</span>
              </div>
            </div>
            <div className="timeline-line"></div>
            <div className="timeline-step">
              <div className="step-icon"><i className="fi fi-rr-bell"></i></div>
              <div className="step-content">
                <span className="step-title">נעדכן אתכם</span>
                <span className="step-desc">כשנהיה מוכנים</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!showForm && !pageMode) {
    return (
      <div id={sectionId} className="project-start-section">
        <div className="project-start-cta">
          <h3>רוצים להישאר מעודכנים?</h3>
          <p>
            אנחנו עובדים על כלים נוספים שיעזרו לכם לקדם תהליך התחדשות עירונית בנכס.
            <br />
            השאירו פרטים ונעדכן אתכם כשנהיה מוכנים.
          </p>
          <button 
            className="btn btn-primary btn-lg"
            onClick={() => {
              AnalyticsService.trackProjectStartCtaClick({ sourceSurface: 'project_start_cta' });
              setShowForm(true);
            }}
          >
            השאירו פרטים
          </button>
        </div>
      </div>
    );
  }

  return (
    <div id={sectionId} className={`project-start-section${pageMode ? ' project-start-section--page' : ''}`}>
      {!pageMode && (
        <button type="button" className="form-dismiss" onClick={() => setShowForm(false)} aria-label="סגור טופס">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      )}
      <div className="project-start-form-container">
        <h3 className="form-title">
          השאירו פרטים ונעדכן אתכם כשנהיה מוכנים לקדם
        </h3>
        
        <form onSubmit={handleSubmit} className="project-start-form">
          {/* Property Details Section */}
          <div className="form-section">
            <h4 className="section-title">פרטי הנכס</h4>
            
            <div className="form-group">
              <label htmlFor="address">כתובת</label>
              <input
                type="text"
                id="address"
                name="address"
                autoComplete="street-address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="הכנס כתובת מלאה"
                className={!address ? "editable-input" : "prefilled-input"}
                disabled={!!address}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="floors">מס' קומות בבניין <span className="required">*</span></label>
                <input
                  type="number"
                  id="floors"
                  name="floors"
                  autoComplete="off"
                  min="1"
                  value={formData.floors}
                  onChange={handleInputChange}
                  className={errors.floors ? 'input-error' : ''}
                  placeholder="למשל: 4"
                />
                {errors.floors && <span className="error-message">{errors.floors}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="apartments">מס' דירות בבניין <span className="required">*</span></label>
                <input
                  type="number"
                  id="apartments"
                  name="apartments"
                  autoComplete="off"
                  min="1"
                  value={formData.apartments}
                  onChange={handleInputChange}
                  className={errors.apartments ? 'input-error' : ''}
                  placeholder="למשל: 8"
                />
                {errors.apartments && <span className="error-message">{errors.apartments}</span>}
              </div>
            </div>
          </div>

          {/* Tenant Organization Section */}
          <div className="form-section">
            <h4 className="section-title">ארגון הדיירים</h4>
            
            <div className="form-group">
              <label>האם קיימת נציגות דיירים מגובשת? <span className="required">*</span></label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="tenantRepresentation"
                    value="yes"
                    checked={formData.tenantRepresentation === 'yes'}
                    onChange={handleInputChange}
                  />
                  <span>כן</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="tenantRepresentation"
                    value="no"
                    checked={formData.tenantRepresentation === 'no'}
                    onChange={handleInputChange}
                  />
                  <span>לא</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="tenantRepresentation"
                    value="in-process"
                    checked={formData.tenantRepresentation === 'in-process'}
                    onChange={handleInputChange}
                  />
                  <span>בתהליך</span>
                </label>
              </div>
              {errors.tenantRepresentation && <span className="error-message">{errors.tenantRepresentation}</span>}
            </div>

            <div className="form-group">
              <label>האם היה ניסיון בעבר לקדם פרויקט בכתובת? <span className="required">*</span></label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="previousAttempt"
                    value="yes"
                    checked={formData.previousAttempt === 'yes'}
                    onChange={handleInputChange}
                  />
                  <span>כן</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="previousAttempt"
                    value="no"
                    checked={formData.previousAttempt === 'no'}
                    onChange={handleInputChange}
                  />
                  <span>לא</span>
                </label>
              </div>
              {errors.previousAttempt && <span className="error-message">{errors.previousAttempt}</span>}
            </div>

            {formData.previousAttempt === 'yes' && (
              <div className="form-group">
                <label>האם מונו עו"ד/מפקח/נציגות?</label>
                <div className="checkbox-group">
                  {['עו"ד', 'מפקח', 'נציגות', 'אחר'].map(role => (
                    <label key={role} className="checkbox-label">
                      <input
                        type="checkbox"
                        name="appointedRoles"
                        id={`appointed-role-${role}`}
                        value={role}
                        checked={formData.appointedRoles.includes(role)}
                        onChange={() => handleRoleToggle(role)}
                      />
                      <span>{role}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="form-group">
              <label>האם היה יזם בתמונה?</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="developerInvolved"
                    value="yes"
                    checked={formData.developerInvolved === 'yes'}
                    onChange={handleInputChange}
                  />
                  <span>כן</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="developerInvolved"
                    value="no"
                    checked={formData.developerInvolved === 'no'}
                    onChange={handleInputChange}
                  />
                  <span>לא</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="developerInvolved"
                    value="unknown"
                    checked={formData.developerInvolved === 'unknown'}
                    onChange={handleInputChange}
                  />
                  <span>לא ידוע</span>
                </label>
              </div>
              {(formData.developerInvolved === 'yes' || formData.developerInvolved === 'unknown') && (
                <textarea
                  id="developerDetails"
                  name="developerDetails"
                  autoComplete="off"
                  value={formData.developerDetails}
                  onChange={handleInputChange}
                  placeholder="פרט (לא חובה)"
                  rows="2"
                  className="textarea-input"
                />
              )}
            </div>
          </div>

          {/* Contact Details Section */}
          <div className="form-section">
            <h4 className="section-title">פרטי קשר</h4>
            
            <div className="form-group">
              <label htmlFor="fullName">שם מלא <span className="required">*</span></label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                autoComplete="name"
                value={formData.fullName}
                onChange={handleInputChange}
                className={errors.fullName ? 'input-error' : ''}
                placeholder="שם פרטי ושם משפחה"
              />
              {errors.fullName && <span className="error-message">{errors.fullName}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">אימייל <span className="required">*</span></label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? 'input-error' : ''}
                  placeholder="example@email.com"
                  dir="ltr"
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="phone">טלפון נייד <span className="required">*</span></label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  autoComplete="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={errors.phone ? 'input-error' : ''}
                  placeholder="050-1234567"
                  dir="ltr"
                />
                {errors.phone && <span className="error-message">{errors.phone}</span>}
              </div>
            </div>
          </div>

          {/* Agreements Section */}
          <div className="form-section agreements-section">
            <div className="form-group">
              <label className={`checkbox-label agreement ${errors.termsAccepted ? 'text-error' : ''}`}>
                <input
                  type="checkbox"
                  name="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={handleInputChange}
                />
                <span>
                  קראתי ואני מאשר/ת את <a href="/terms" target="_blank">מדיניות השימוש והפרטיות</a> <span className="required">*</span>
                </span>
              </label>
              {errors.termsAccepted && <span className="error-message">{errors.termsAccepted}</span>}
            </div>

            <div className="form-group">
              <label className="checkbox-label agreement">
                <input
                  type="checkbox"
                  name="updatesAccepted"
                  checked={formData.updatesAccepted}
                  onChange={handleInputChange}
                />
                <span>אני מסכים/מה לקבלת עדכונים בנושא התקדמות הפנייה (אופציונלי)</span>
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <div className="form-actions">
            <button 
              type="submit" 
              className="btn btn-primary btn-lg"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'שולח...' : 'שליחת פרטים'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectStartForm;
